// entrypoint.tsx

import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraFieldConfigurationAri } from '@atlassian/ari/jira';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import parameters from './__generated__/FieldsPageViewQuery$parameters';
import type { JiraFieldScopeType } from './__generated__/FieldsPageViewQuery.graphql';

// Backwards compatibility with old URL params
const CustomFieldStatus = {
	TRASHED: 'trashed',
	ACTIVE: 'active',
} as const;

type InitialTab = 'active' | 'deleted';

export const globalSettingsIssueFieldsManagementRouteEntryPoint = createEntryPoint({
	root: JSResourceForUserVisible(
		() =>
			import(
				/* webpackChunkName: "async-jira-admin-pages-fields-page" */ './GlobalSettingsAdminPagesFields.tsx'
			),
	),
	getPreloadProps: ({
		tenantContext: { cloudId, activationId, isAdmin },
		context: { query },
	}: EntryPointRouteParams) => {
		const sidebarId = query.sidebarId?.toUpperCase();
		const sidebarView = query.sidebarView?.toLowerCase();
		const fieldId = query.fieldId;
		const initialTab: InitialTab =
			query.customFieldStatus?.toLowerCase() === CustomFieldStatus.TRASHED ? 'deleted' : 'active';

		const preloadFieldNodeId = fieldId
			? (() => {
					try {
						return JiraFieldConfigurationAri.create({
							fieldConfigurationId: fieldId,
							activationId,
							siteId: cloudId,
						}).toString();
					} catch (error) {
						return '';
					}
				})()
			: '';

		const fieldScope: JiraFieldScopeType = expValEquals(
			'centralised-fields-show-edit-tmp-exp',
			'isEnabled',
			true,
			false,
		)
			? 'ALL'
			: 'GLOBAL';

		return {
			queries: {
				globalFields: {
					options: {
						fetchPolicy: 'network-only' as const,
					},
					parameters,
					variables: {
						cloudId,
						isAdmin,
						includeIsForge: fg('modify_forge_field_type_display_name') || false,
						preloadFieldNodeId,
						shouldPreloadField: !!preloadFieldNodeId,
						includeInstalledByAppName: fg('cfm_add_app_name') || false,
						fieldScope,
						includeFieldSchemeOperations: fg('jira_efa_for_cmp_m2_open-beta'),
						// Never included for deleted fields
						includeFieldSchemeOperationsForDeleted: false,
					},
				},
			},
			extraProps: {
				initialTab,
				sidebarId,
				sidebarView,
				isAdmin,
				fieldNodeId: preloadFieldNodeId,
			},
		};
	},
});
